import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventCatalog, fetchJobs } from '../api/client.js';
import { GROUPS, GROUP_LABEL } from '../lib/rules.js';
import * as E from '../lib/engine.js';
import { LUCK_TRANSITIONS } from '../lib/luckTransitions.js';
import { levelAfterChange } from '../lib/jobChange.js';
import { luckScene } from '../lib/luckScene.js';
import SceneView from '../components/SceneView.jsx';
import PillarsCard from '../components/PillarsCard.jsx';
import LifeCard from '../components/LifeCard.jsx';
import { SCENES } from '../data/scenes.js';
import DaewoonFlow from '../components/DaewoonFlow.jsx';
import MeetModal from '../components/MeetModal.jsx';
import WhyLine from '../components/WhyLine.jsx';
import PeopleView from '../components/PeopleView.jsx';
import RelationshipModal from '../components/RelationshipModal.jsx';
import { DesktopToolbar } from '../components/Desktop.jsx';
import '../extra.css';
import '../life-play.css';
import { CHOICE_KIND_LABEL, OUTCOME_LABEL, choiceEffectText } from '../lib/events.js';
import { choiceTimeCost, concreteOpportunities, lifePhase, personAvailability, timePlan } from '../lib/life.js';

const SPEEDS = [['1배속', 1200], ['2배속', 600], ['4배속', 250], ['8배속', 120], ['16배속', 60]];
const FAST_FROM = 3; // 8배속부터는 해 넘김 연출을 생략한다
const STEM_KO = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
const BRANCH_KO = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];

export default function PlayPage({ run, onChange, onEnd }) {
  const [focus, setFocus] = useState(null);
  const [focusUnits, setFocusUnits] = useState(2);
  const [participantId, setParticipantId] = useState('');
  const [auto, setAuto] = useState(false);
  const [speed, setSpeed] = useState(0);
  const [event, setEvent] = useState(null);     // 선택을 기다리는 이벤트
  const [offers, setOffers] = useState(null);   // 첫 직업 후보
  const [jobChange, setJobChange] = useState(null); // 전직 제안 { curr, offer }
  const [meet, setMeet] = useState(null);       // 새로운 만남 후보
  const [relationship, setRelationship] = useState(null);
  const [showPeople, setShowPeople] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [until, setUntil] = useState(false);    // 다음 결정까지 빠르게 넘기기
  const [banner, setBanner] = useState(null);   // 해가 바뀔 때 세운 안내
  const [deltas, setDeltas] = useState({});     // 방금 바뀐 능력치
  const prevStats = useRef(run.stats);
  const s = run;
  const op = E.opensFor(s, s.age);
  const opportunities = concreteOpportunities(s, op);
  const focusing = s.age >= 15;
  const plan = timePlan(s, { focus, focusUnits });
  const eventRun = event?.curr ?? s;
  const eventPlan = timePlan(eventRun, { focus: event?.focus, focusUnits });
  const companions = E.nearPeople(eventRun).map((person) => ({ person, availability: personAvailability(eventRun, person) }));
  const modalOpen = !!event || !!offers || !!jobChange || !!meet || !!relationship || showPeople || (s.branchPending && !auto);

  useEffect(() => { setFocus(null); setFocusUnits(2); setParticipantId(''); }, [s.age]);

  const [toast, setToast] = useState(null);     // 방금 고른 선택의 결과(성공·실패)를 바로 보여 준다
  const finish = useCallback((next) => {
    onChange(next);
    const year = next.age - 1, entry = (next.log ?? []).find((l) => l.age === year && l.kind === 'event');
    if (entry) {
      const effects = Object.entries(entry.effects ?? {}).filter(([, v]) => Number.isFinite(v) && v !== 0)
        .map(([field, v]) => `${GROUP_LABEL[field] ?? field} ${v > 0 ? '+' : ''}${v}`).join(' · ');
      const money = next.life?.economy?.sceneLog?.at(-1);
      const moneyText = money && money.age === year && money.money ? money.money : '';
      setToast({ key: `${year}-${entry.eventId}`, age: year, outcome: entry.outcome ?? (entry.failed ? 'failure' : 'success'),
        text: entry.text, effects: [effects, moneyText].filter(Boolean).join(' · '), narrative: entry.narrative ?? '' });
    }
    if (next.status === 'ended') onEnd(next);
  }, [onChange, onEnd]);

  // 한 해 진행: 직업·갈래 선택이 필요하면 그 모달을 먼저 띄운다
  const step = useCallback(async (curr, chosenFocus, isAuto = false, stopAtDecision = false) => {
    setBusy(true); setError('');
    const stopRun = () => { setAuto(false); setUntil(false); };
    try {
      const refreshed = E.refreshSceneData(curr, SCENES);
      if (refreshed !== curr) { curr = refreshed; onChange(curr); }
      if (curr.age >= 15 && !curr.referenceData) {
        // 새 형식 장면(v2)을 쓰는 진행은 서버 이벤트 대신 장면 데이터를 쓴다.
        const [events, jobs] = await Promise.all([curr.sceneModel ? Promise.resolve(SCENES) : fetchEventCatalog(), fetchJobs()]);
        curr = E.attachReferenceData(curr, { events, jobs });
        onChange(curr);
      }
      if (E.needsJob(curr)) {
        const list = E.jobOffers(curr, curr.referenceData.jobs);
        if (isAuto && !stopAtDecision) {
          const pick = E.autoJob(curr, list);
          return finish(E.annotateAuto(curr, E.chooseJob(curr, pick?.job ?? list[0]), pick?.reason, (l) => l.kind === 'job'));
        }
        if (isAuto) stopRun();
        setOffers(list); return;
      }
      if (curr.branchPending) {
        if (!isAuto) return;                               // 모달이 처리
        if (stopAtDecision) { stopRun(); return; }
        const pick = E.autoBranch(curr);
        return finish(E.annotateAuto(curr, E.chooseBranch(curr, pick.idx), pick.reason, (l) => l.kind === 'job'));
      }
      const pending = E.pendingJobOffer(curr, curr.referenceData?.jobs);
      if (curr.pendingJobSwitch && !pending) curr = { ...curr, pendingJobSwitch: null }; // 후보가 없으면 그냥 지나간다
      if (pending) {
        if (isAuto && !stopAtDecision) {
          const pick = E.autoJob(curr, pending.offers);
          const decided = pick ? E.changeJob(curr, pick.job, pending) : E.declineJobChange(curr, pending);
          return finish(E.annotateAuto(curr, decided, pick?.reason ?? '직업 전환: 지금 일을 유지', (l) => l.kind === 'job'));
        }
        if (isAuto) stopRun();
        setJobChange({ curr, offer: pending }); return;
      }
      const change = E.jobChangeOffer(curr, curr.referenceData?.jobs);
      if (change) {
        if (isAuto && !stopAtDecision) {
          const pick = E.autoJobChange(curr, change);
          const decided = pick ? E.changeJob(curr, pick.job, change) : E.declineJobChange(curr, change);
          return finish(E.annotateAuto(curr, decided, pick?.reason ?? '전직: 지금 일이 더 어울려 유지', (l) => l.kind === 'job'));
        }
        if (isAuto) stopRun();
        setJobChange({ curr, offer: change }); return;
      }
      const m = E.meetOffer(curr);
      if (m) {
        if (!isAuto || stopAtDecision) { if (isAuto) stopRun(); setMeet({ curr, offer: m }); return; }
        curr = E.autoMeet(curr, m);
      }
      const focusPick = curr.age >= 15 && isAuto && !stopAtDecision ? E.autoFocusPick(curr) : null;
      const f = curr.age >= 15 ? (focusPick ? focusPick.field : chosenFocus) : null;
      const units = isAuto && !stopAtDecision ? 1 : focusUnits;
      const request = E.relationshipOffer(curr);
      if (request) {
        if (!isAuto || stopAtDecision) {
          if (isAuto) stopRun();
          setRelationship({ curr, offer: request, focus: f }); return;
        }
        const available = timePlan(curr, { focus: f, focusUnits: units }).remaining;
        const rk = E.autoRelationshipKind(curr, available);
        return finish(E.annotateAuto(curr, E.resolveRelationship(curr, { offer: request,
          kind: rk.kind, focusField: f, focusUnits: units }), rk.reason, (l) => l.kind === 'people'));
      }
      const cands = curr.age >= 15 ? curr.referenceData.events : [];
      // 인생 사건(결혼·출산·주택 등)이 있는 해에는 일반 사건 대신 그 사건을 올린다.
      const ev = E.lifeEvent(curr) ?? (curr.sceneModel ? E.drawScene(curr, cands) : E.drawEvent(curr, cands, f, units));
      if (ev && (!isAuto || stopAtDecision)) {
        if (isAuto) stopRun();
        setParticipantId(''); setEvent({ curr, ev, focus: f }); return;
      }
      const choicePick = ev ? E.autoChoiceDetail(curr, ev, f, units) : null;
      let next = E.resolveYear(curr, { focus: f, focusUnits: units, event: ev,
        choiceIdx: choicePick ? choicePick.idx : null });
      if (isAuto && !stopAtDecision) {
        if (choicePick) next = E.annotateAuto(curr, next, choicePick.reason, (l) => l.kind === 'event' && l.age === curr.age);
        if (focusPick) next = E.annotateAuto(curr, next, focusPick.reason, (l) => l.kind === 'focus' && l.age === curr.age, { trace: false });
      }
      finish(next);
    } catch (e) {
      setError(e.message); setAuto(false); setUntil(false);
    } finally { setBusy(false); }
  }, [finish, focusUnits, onChange]);

  const next = () => { if (!busy && !modalOpen && s.status === 'play') step(s, focus); };
  const nextDecision = () => {
    if (busy || modalOpen || s.status !== 'play') return;
    setSpeed(SPEEDS.length - 1); setUntil(true); setAuto(true);
  };

  // 자동 진행: 기질대로 선택하며 일정 간격으로 한 해씩
  useEffect(() => {
    if (!auto || busy || modalOpen || s.status !== 'play') return undefined;
    const t = setTimeout(() => step(s, until ? focus : null, true, until), SPEEDS[speed][1]);
    return () => clearTimeout(t);
  }, [auto, busy, modalOpen, s, speed, step, until, focus]);

  // 능력치가 바뀌면 바 옆에 변화량을 잠깐 띄운다
  useEffect(() => {
    const d = {};
    for (const g of GROUPS) {
      const x = Math.round((s.stats[g] - prevStats.current[g]) * 10) / 10;
      if (x !== 0) d[g] = x;
    }
    prevStats.current = s.stats;
    if (!Object.keys(d).length) return undefined;
    setDeltas(d);
    const t = setTimeout(() => setDeltas({}), 1800);
    return () => clearTimeout(t);
  }, [s.stats]);

  // 해가 바뀌면 그해의 세운(간지)과 열린 기회를 잠깐 보여 준다. 빠른 속도에서는 생략
  const fast = auto && speed >= FAST_FROM;
  // 결과 안내는 잠깐 보였다가 사라진다(빠른 배속에서는 더 짧게)
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), fast ? 1400 : 4500);
    return () => clearTimeout(t);
  }, [toast, fast]);
  useEffect(() => {
    if (s.age === 0 || fast) { setBanner(null); return undefined; }
    const year = s.birthYear + s.age;
    const gz = `${STEM_KO[(((year - 4) % 10) + 10) % 10]}${BRANCH_KO[(((year - 4) % 12) + 12) % 12]}`;
    const luck = E.luckRecord(s, s.age);
    const label = luck?.type ? LUCK_TRANSITIONS.labels?.[luck.type] : '';
    setBanner({ key: s.age, year, gz, age: s.age, open: E.opensFor(s, s.age).open,
      luckChange: luck && luck.yearsSince === 0 ? `대운이 바뀝니다 · ${luck.ten} 운${label ? ` · ${label}` : ''}` : '' });
    const t = setTimeout(() => setBanner(null), 1700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.age, fast]);

  const pickChoice = (i) => {
    try {
      const { curr, ev, focus: f } = event;
      const nextRun = E.resolveYear(curr, { focus: f, focusUnits, event: ev, choiceIdx: i,
        participantId: participantId || null });
      setEvent(null); setParticipantId(''); finish(nextRun);
    } catch (e) { setError(e.message); }
  };
  const pickRelationship = (choice) => {
    try {
      const nextRun = E.resolveRelationship(relationship.curr, { ...choice,
        focusField: relationship.focus, focusUnits });
      setRelationship(null); onChange(nextRun);
    } catch (e) { setError(e.message); }
  };
  const pickMeet = (r) => { const curr = meet.curr; setMeet(null); onChange(E.resolveMeet(curr, r)); };
  const pickJob = (job) => { setOffers(null); onChange(E.chooseJob(s, job)); };
  const pickBranch = (idx) => onChange(E.chooseBranch(s, idx));
  const pickJobChange = (job) => { const { curr, offer } = jobChange; setJobChange(null); onChange(job ? E.changeJob(curr, job, offer) : E.declineJobChange(curr, offer)); };

  return (
    <div className="play">
      {toast && <div key={toast.key} className={`result-toast result-toast-${toast.outcome}`} role="status" onClick={() => setToast(null)}>
        <b className="result-toast-badge">{OUTCOME_LABEL[toast.outcome] ?? '결과'}</b>
        <span className="result-toast-text">{toast.age}세 · {toast.text}</span>
        {toast.effects && <span className="result-toast-effects">{toast.effects}</span>}
        {toast.narrative && <span className="result-toast-narrative">{toast.narrative}</span>}
      </div>}
      <DesktopToolbar><div className="topbar" role="group" aria-label="플레이 제어">
        <b className="age">{s.age}세</b>
        {s.job && <span className="chip">직업 · {E.jobTitle(s.job)} <small>({s.job.level}단계 · {s.job.name})</small></span>}
        <div className="grow" />
        <button onClick={() => { setAuto(false); setShowPeople(true); }}>곁의 사람 {E.nearPeople(s).length}/{E.MAX_PEOPLE}</button>
        <button onClick={() => { setAuto(false); setUntil(false); }} disabled={!auto}>일시정지</button>
        <div className="play-speed-controls" role="group" aria-label="진행 배속">{SPEEDS.map(([label], i) => (
          <button key={label} className={auto && !until && speed === i ? 'on' : ''} onClick={() => { setSpeed(i); setUntil(false); setAuto(true); }}>{label}</button>
        ))}</div>
        <button className={until ? 'on' : ''} onClick={nextDecision}>다음 결정까지 »</button>
        <label className="inline"><input type="checkbox" checked={auto && !until} onChange={(e) => { setUntil(false); setAuto(e.target.checked); }} /> 자동 결정 (기질 따름)</label>
      </div></DesktopToolbar>

      {focusing && <section className="panel life-phase"><Budget plan={plan} /></section>}

      <div className="stage">
        <SceneView saju={s.saju} run={s} />
        {banner && (
          <div key={banner.key} className="yearbanner">
            <b>{banner.year}년 {banner.gz}년</b>
            <span>{banner.age}세 · {s.age < 15 ? lifePhase(s.age).label : banner.open.length ? `열린 기회 ${banner.open.map((g) => GROUP_LABEL[g]).join(' · ')}` : '열린 기회 없음'}{banner.luckChange && s.age >= 15 ? ` · ${banner.luckChange}` : ''}</span>
          </div>
        )}
      </div>

      <main className="playgrid">
          <section className="panel">
            <h3>나의 영역</h3>
            {GROUPS.map((g) => (
              <div className="bar" key={g}>
                <span>{g} {GROUP_LABEL[g]}</span>
                <div className="track"><div style={{ width: `${s.stats[g]}%` }} /></div>
                <b>{s.stats[g]}</b>
                {deltas[g] != null && <i key={`${s.age}-${g}`} className={`delta ${deltas[g] > 0 ? 'up' : 'down'}`}>{deltas[g] > 0 ? '+' : ''}{deltas[g]}</i>}
              </div>
            ))}
          </section>
          <LifeCard s={s} />
          <PillarsCard saju={s.saju} />
        <section className="panel">
          <h3>올해 열린 기회</h3>
          <div className="life-opportunities">
            {opportunities.map((item) => <article key={item.field} className="life-opportunity">
              <b>{item.label} · {item.title}</b><p>{item.description}</p>
            </article>)}
            {!opportunities.length && <p className="life-explanation">{focusing ? '올해는 열린 기회가 없습니다. 원하는 분야의 준비를 이어갈 수 있습니다.' : '15세부터 기회를 활용할 분야를 직접 고릅니다.'}</p>}
          </div>
          {focusing ? (
            <>
              <h3>올해 집중할 분야</h3>
              <div className="chips">
                {GROUPS.map((g) => (
                  <button key={g} className={focus === g ? 'on' : ''} onClick={() => setFocus(focus === g ? null : g)}>{GROUP_LABEL[g]}</button>
                ))}
              </div>
              {focus && <FocusAllocation value={focusUnits} onChange={setFocusUnits} />}
              <p className="life-explanation">활동 시간은 한 해 3칸입니다. 집중·사건·사람의 부탁에 나눠 씁니다. 시간 0칸은 올해 집중 효과를 받지 않습니다.</p>
              {!plan.valid && <p className="error">{plan.reason}</p>}
            </>
          ) : <p className="life-explanation">이 시기에는 생활 기록을 남깁니다. 선택에 따른 능력치 성장은 15세부터 시작합니다.</p>}
          <button className="primary wide" onClick={nextDecision} disabled={busy || modalOpen || auto || !plan.valid || s.status !== 'play'}>
            {busy ? '진행 중…' : '다음 결정까지 진행'}
          </button>
          <button className="wide" onClick={next} disabled={busy || modalOpen || auto || !plan.valid || s.status !== 'play'}>{s.age}세 한 해 보내기</button>
          <p className="life-explanation">새로운 만남·관계 부탁·직업·사건 선택이 나오면 진행을 멈춥니다.</p>
          {s.log[0]?.kind === 'life' && <p className="life-latest">{s.log[0].age}세 · {s.log[0].text}</p>}
          {error && <p className="error">{error}</p>}
        </section>
      </main>

      <DaewoonFlow saju={s.saju} age={s.age} birthYear={s.birthYear} scene={luckScene(s)?.text ?? ''} />

        <section className="panel logpanel">
          <h3>기록</h3>
          <ul className="log">
            {s.log.slice(0, 40).map((l, i) => (
              <li key={i}><b>{l.age}세</b> · {l.text}{l.result && <em> ({l.result})</em>}
                {l.autoReason && <div className="auto-reason">자동 결정 · {l.autoReason}</div>}
                {l.narrative && <div className="event-narrative">{l.narrative}</div>}
              </li>
            ))}
            {!s.log.length && <li className="muted">아직 기록이 없어요</li>}
          </ul>
        </section>

      {meet && <MeetModal run={meet.curr} offer={meet.offer} onResolve={pickMeet} />}
      {relationship && <RelationshipModal run={relationship.curr} offer={relationship.offer}
        focusField={relationship.focus} focusUnits={focusUnits} onFocusUnitsChange={setFocusUnits}
        onFocusFieldChange={(field) => setRelationship((value) => ({ ...value, focus: field }))}
        onResolve={pickRelationship} />}
      {showPeople && <PeopleView run={s} onDrift={(id) => onChange(E.driftPerson(s, id))} onClose={() => setShowPeople(false)} />}

      {event && (
        <Modal title={`${s.age}세 · 갈림길`} badge={op.open.includes(event.ev.field) ? `열린 기회: ${event.ev.field}` : null}>
          <h2>{event.ev.title}</h2>
          {event.ev.trigger && <p className="event-context">{event.ev.trigger.age}세 선택의 후속 사건 · 이전 결과: {OUTCOME_LABEL[event.ev.trigger.outcome]}</p>}
          {event.ev.natalReason && event.ev.category !== 'life' && <p className="event-context">{event.ev.category === 'scene' ? `사주 · ${event.ev.natalReason}` : event.ev.natalReason}</p>}
          <WhyLine why={event.ev.why} />
          <p className="muted" style={{ whiteSpace: 'pre-line' }}>{event.ev.description}</p>
          {event.ev.stat?.text && <p className="event-context">{event.ev.stat.text}{event.ev.stat.source ? ` (${event.ev.stat.source})` : ''}</p>}
          {event.ev.category !== 'scene' && event.ev.category !== 'life' && <div className="life-event-plan">
            <Budget plan={eventPlan} />
            <label>올해 집중할 분야
              <select value={event.focus ?? ''} onChange={(e) => setEvent((value) => ({ ...value, focus: e.target.value || null }))}>
                <option value="">집중 분야를 정하지 않는다</option>
                {GROUPS.map((field) => <option key={field} value={field}>{GROUP_LABEL[field]}</option>)}
              </select>
            </label>
            {event.focus ? <>
              <p>올해 집중 · {GROUP_LABEL[event.focus]}{focusUnits === 0 ? ' · 집중 효과 없음' : ''}</p>
              <FocusAllocation value={focusUnits} onChange={setFocusUnits} />
            </> : <p>올해 집중 분야를 정하지 않았습니다. 사건에 쓸 시간을 남겨 둡니다.</p>}
            <p>각 선택에 표시된 시간을 사용합니다. 작은 범위의 시작과 재조정은 1칸으로 진행할 수 있습니다. 집중 시간을 줄여 사건에 쓸 수 있습니다.</p>
            {companions.length > 0 && <label>함께할 사람
              <select value={participantId} onChange={(e) => setParticipantId(e.target.value)}>
                <option value="">혼자 진행한다</option>
                {companions.map(({ person, availability }) => <option key={person.id} value={person.id} disabled={!availability.available}>
                  {person.name} · {person.role} · {availability.label}{availability.available ? '' : ` (${availability.reason})`}
                </option>)}
              </select>
            </label>}
          </div>}
          {event.ev.choices.map((c, i) => {
            const choicePlan = timePlan(eventRun, { focus: event.focus, focusUnits, event: event.ev, choiceIdx: i });
            return <button key={i} className="choice" disabled={!choicePlan.valid} onClick={() => pickChoice(i)}>
              <span className="num">{i + 1}</span>
              <span className="grow choice-copy">
                {(event.ev.category === 'scene' || event.ev.category === 'life') && c.hint ? <>
                  <b>{c.label}</b>
                  <small>{c.hint}{c.kind !== 'decline' && c.risk > 0 ? ` · 실패 확률 ${Math.round(c.risk * 100)}%` : ''}</small>
                </> : <>
                  <span className="choice-kind">{CHOICE_KIND_LABEL[c.kind ?? 'act']}</span>
                  <b>{c.label}</b>
                  <small>기본 효과: {choiceEffectText(c)}</small>
                  {c.kind !== 'decline' && c.risk > 0 && (
                    <small>실패 확률 {Math.round(c.risk * 100)}% · 실패 시 {choiceEffectText(c, true)}</small>
                  )}
                </>}
                {!choicePlan.valid && <small>{choicePlan.reason}</small>}
              </span>
              {event.ev.category !== 'scene' && event.ev.category !== 'life' && <span className="life-choice-cost">시간 {choiceTimeCost(c)}칸</span>}
            </button>;
          })}
          {error && <p className="error">{error}</p>}
          {event.ev.category === 'scene' || event.ev.category === 'life' ? <p className="muted small">선택은 되돌릴 수 없습니다.</p> : <p className="muted small">선택은 되돌릴 수 없습니다. {s.luckModel === 'environment' ? '양수 효과는 현재 능력치와 기세에 따라 달라지고, 열린 기회는 어떤 제안이 나오는지에 영향을 줍니다.' : '양수 효과는 현재 능력치·기세·열린 기회에 따라 달라집니다.'} 하지 않기는 함께할 사람을 선택해도 공동 행동으로 기록하지 않습니다.</p>}
        </Modal>
      )}

      {offers && (
        <Modal title={`${s.age}세 · 첫 직업`}>
          <h2>어떤 일을 시작할까요?</h2>
          <p className="muted">지금까지 쌓아 온 영역이 높은 분야에서 후보가 나왔어요.</p>
          {s.sceneModel && s.life?.education !== 'college' && <p className="muted small">대학에 가지 않아 학력이 필요한 직업(의사, 법조인 등)은 후보에서 빠졌어요.</p>}
          {offers.map((j) => (
            <button key={j.id} className="choice" onClick={() => pickJob(j)}>
              <span className="grow"><b>{j.name}</b> <small>{j.levels[0]} → {j.levels[2]}</small></span><small>{j.field}</small>
            </button>
          ))}
          <button className="choice" onClick={() => pickJob(null)}><span className="grow">지금은 일하지 않는다</span></button>
        </Modal>
      )}

      {jobChange && (
        <Modal title={`${s.age}세 · 전직 제안`}>
          <h2>{jobChange.offer.pending ? '새 일 후보가 나왔어요' : '다른 일을 시작할 기회가 왔어요'}</h2>
          {jobChange.offer.pending && <p className="muted">{jobChange.offer.pending === 'same' ? '같은 분야에서 옮길 수 있는 자리예요.' : '다른 분야로 옮길 수 있는 일이에요.'} 옮기면 단계가 한 단계 낮아집니다.</p>}
          {!jobChange.offer.pending && <p className="muted">대운이 바뀌는 시기예요. 지금 일보다 이 시기의 방향과 더 어울리는 일이 있어요. 전직하면 단계가 한 단계 낮아지고, 4단계 갈래는 새 일에서 다시 고르게 돼요.</p>}
          {jobChange.offer.offers.map((j) => (
            <button key={j.id} className="choice" onClick={() => pickJobChange(j)}>
              <span className="grow"><b>{j.name}</b> <small>{j.levels[levelAfterChange(jobChange.curr.job.level) - 1]}부터</small>
                {j.reason && <small className="muted"> · {j.reason}</small>}</span><small>{j.field}</small>
            </button>
          ))}
          <button className="choice" onClick={() => pickJobChange(null)}><span className="grow">지금 일을 계속한다</span></button>
        </Modal>
      )}

      {s.branchPending && !event && !offers && !jobChange && (
        <Modal title={`${s.age}세 · 갈림길`}>
          <h2>{s.job.name}으로 쌓아 온 경력, 어느 길로 갈까요?</h2>
          {E.branchOrder(s).map((b, i) => (
            <button key={b.idx} className="choice" onClick={() => pickBranch(b.idx)}>
              <span className="grow"><b>{b.name}</b></span><small>{b.trait}{i === 0 ? ' · 성향에 가까움' : ''}</small>
            </button>
          ))}
        </Modal>
      )}
    </div>
  );
}

function FocusAllocation({ value, onChange }) {
  return <div className="life-focus-controls" aria-label="올해 집중 시간">
    {[[2, '집중 2칸'], [1, '가볍게 1칸'], [0, '이번 해는 쉬기 0칸']].map(([units, label]) => (
      <button key={units} type="button" className={value === units ? 'on' : ''}
        aria-pressed={value === units} onClick={() => onChange(units)}>{label}</button>
    ))}
  </div>;
}

function Budget({ plan }) {
  return <div className="life-budget" aria-label={`올해 활동 시간 ${plan.capacity}칸 중 ${plan.remaining}칸 남음`}>
    {Array.from({ length: plan.capacity }, (_, i) => <i key={i} className={i >= plan.remaining ? 'spent' : ''} />)}
    <span>남은 시간 <b>{plan.remaining}/{plan.capacity}칸</b></span>
  </div>;
}

function Modal({ title, badge, children }) {
  return (
    <div className="overlay">
      <div className="card modal">
        <div className="row" style={{ alignItems: 'center' }}>
          <span className="agechip">{title}</span><div className="grow" />{badge && <span className="chip">{badge}</span>}
        </div>
        {children}
      </div>
    </div>
  );
}
